import React, { useRef, useCallback, useEffect, FC } from "react";
import styles from "../styles/Announcements.module.scss";
import { Loader } from "Components/UI/Loader";
import { useAnnouncements } from "../useAnnouncementsModal";
import { useAppDispatch } from "Redux/store";
import {
  getAnnouncementsList,
  getUnreadCount,
  markAsReadAll,
} from "Redux/Announcements/reducer";
import { useSelector } from "react-redux";
import {
  selectAnnouncementsListCount,
  selectAnnouncementsListError,
  selectAnnouncementsListHasMore,
  selectAnnouncementsListSuccess,
} from "Redux/Announcements/slice";
import AnnouncementItem from "./AnnouncementItem/AnnouncementItem";
import { LIMIT_PAGE } from "Shared/Constants/app";
import { SideModal } from "Components/SideModal/SideModal";
import { useModal } from "@ebay/nice-modal-react";
import ImageShowModal from "Components/ImageShowModal/ImageShowModal";

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

export const AnnouncementsModal: FC<Props> = ({ onClose, isOpen }) => {
  const { loading, announcements } = useAnnouncements();
  const dispatch = useAppDispatch();
  const count = useSelector(selectAnnouncementsListCount);
  const hasMore = useSelector(selectAnnouncementsListHasMore);
  const error = useSelector(selectAnnouncementsListError);
  const loaded = useSelector(selectAnnouncementsListSuccess);
  const modal = useModal(ImageShowModal);
  const pageRef = useRef(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadPage = useCallback(
    (page: number) => {
      pageRef.current = page;
      dispatch(
        getAnnouncementsList({
          params: { limit: LIMIT_PAGE, offset: (page - 1) * LIMIT_PAGE },
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  // Loads the next page when the end of the list scrolls into view. The
  // observer is rebuilt after every page, so one sighting is one request.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        loadPage(pageRef.current + 1);
      },
      { rootMargin: "100px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadPage]);

  useEffect(() => {
    if (isOpen) {
      dispatch(
        markAsReadAll({
          onSuccess: () => dispatch(getUnreadCount()),
        }),
      );
    }
  }, [isOpen, dispatch]);

  return (
    <SideModal
      extraConditionToOutsideClick={modal.visible}
      className={styles.modal}
      isOpen={isOpen}
      onClose={onClose}
      key={"announcements"}
    >
      <div>
        <div className={styles.countWrapper}>
          <h2 className={styles.countTitle}>
            Announcements in EasyFleet
            <span className={styles.count}>{count}</span>
          </h2>
        </div>

        <div>
          {announcements.length > 0 && (
            <ul className={styles.announcementWrapper}>
              {announcements.map((announcement) => (
                <AnnouncementItem
                  key={announcement.id}
                  announcement={announcement}
                />
              ))}
            </ul>
          )}
          {loaded && !loading && announcements.length === 0 && (
            <p className={styles.state}>No announcements yet.</p>
          )}
          {loading && (
            <div
              className={styles.loader}
              role="status"
              aria-label="Loading announcements"
            >
              <Loader />
            </div>
          )}
          {error && !loading && (
            <div className={styles.state} role="alert">
              <p>Announcements could not be loaded.</p>
              <button
                type="button"
                className={styles.textButton}
                onClick={() => loadPage(pageRef.current)}
              >
                Try again
              </button>
            </div>
          )}
          <div ref={sentinelRef} aria-hidden="true" />
        </div>
      </div>
    </SideModal>
  );
};
